import { useEffect, useRef, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

/**
 * 地理编码配置。
 *
 * ★ 没配这个,凡是建单时没给坐标的单**一律进复核队列** ——
 *   而导入一次三百单,意味着有人要在地图上点三百次。
 *   所以这一屏第一句话就得说明「不配会怎样」,
 *   而不是摆一个空输入框等人猜。
 *
 * ★ 密钥这一栏收的是**引用**(`env:名字`),不是密钥本身。
 *   这件事必须写在输入框边上 —— 不写的话人一定会直接粘一个 key 进去,
 *   然后它就永久躺在数据库里,连同每一次备份、每一次导出。
 */

interface Config {
  url: string | null;
  keyRef: string | null;
}

/** 常见服务的模板 —— 让人照着改,而不是从零拼一个 URL */
const PRESETS: Array<{ name: string; url: string; note: string }> = [
  {
    name: 'Google',
    url: 'https://maps.googleapis.com/maps/api/geocode/json?address={address}&key={key}',
    note: '密钥用 env 引用,别直接写进 URL',
  },
  {
    name: '高德',
    url: 'https://restapi.amap.com/v3/geocode/geo?address={address}&key={key}',
    note: '返回的是 GCJ02 坐标,和地图 Provider 要对上',
  },
  {
    name: '自建 Nominatim',
    url: 'https://nominatim.example.com/search?q={address}&format=json',
    note: '公共实例的使用政策禁止批量调用 —— 自己搭一个',
  },
];

export function GeocodeSettings({ canManage }: { canManage: boolean }) {
  const [cfg, setCfg] = useState<Config>({ url: null, keyRef: null });
  const [url, setUrl] = useState('');
  const [keyRef, setKeyRef] = useState('');
  const [fbUrl, setFbUrl] = useState('');
  const [fbKeyRef, setFbKeyRef] = useState('');
  /*
   * ★ 人动过表单没有。
   *
   *   这一屏进来时会去 /auth/me 读一次配置回填 —— 而那一次请求
   *   可能在人已经开始打字之后才回来,回来就把他填的清掉了。
   *   表现是「我明明填了,一保存却是空的」,而且只在网慢的时候出现。
   *   (取货点表单踩过同一个坑,这里是第二次。)
   */
  const dirtyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [probeAddr, setProbeAddr] = useState('');
  const [probe, setProbe] = useState<{ ok: boolean; text: string } | null>(null);

  /**
   * 拿一个真地址当场试一次。
   *
   * ★ 没有这一步的话,验证方式是建一张单、等异步补齐、再去看它
   *   有没有进复核队列 —— 而失败原因只在服务器日志里。
   *   刚配好 key 的人撞得最多的是 REQUEST_DENIED(Geocoding API 没开通,
   *   或者 key 限制了来源),那句话必须让配的人自己看见。
   */
  async function test() {
    setBusy(true);
    setProbe(null);
    try {
      const r = await api<{
        ok: boolean;
        lat?: number;
        lng?: number;
        accuracy?: string;
        reason?: string;
        source?: string;
      }>(
        '/settings/geocode/test',
        { method: 'POST', body: { address: probeAddr.trim() } },
      );
      setProbe(
        r.ok
          ? {
              ok: true,
              // ★ 「通了」还不够:走的是主用还是备用,是两种不同的「通」。
              //   一直走备用意味着主用那家出了问题,而没人会主动去查
              text: `通了:${r.lat}, ${r.lng}(精度 ${r.accuracy}，${
                r.source === 'fallback' ? '走的是**备用**服务' : '走的是主用服务'
              }）`,
            }
          : { ok: false, text: r.reason ?? '没通,但服务没说原因' },
      );
    } catch (e) {
      setProbe({ ok: false, text: e instanceof ApiFailure ? e.message : '请求失败' });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void api<{
      tenant: {
        geocodeUrl?: string | null;
        geocodeKeyRef?: string | null;
        geocodeFallbackUrl?: string | null;
        geocodeFallbackKeyRef?: string | null;
      };
    }>('/auth/me')
      .then((me) => {
        const c = { url: me.tenant.geocodeUrl ?? null, keyRef: me.tenant.geocodeKeyRef ?? null };
        setCfg(c);
        // ★ 人已经在填了就别回填 —— 回来晚的那次会把他填的清掉
        if (dirtyRef.current) return;
        setUrl(c.url ?? '');
        setKeyRef(c.keyRef ?? '');
        setFbUrl(me.tenant.geocodeFallbackUrl ?? '');
        setFbKeyRef(me.tenant.geocodeFallbackKeyRef ?? '');
      })
      .catch(() => undefined);
  }, []);

  async function save(next: {
    url: string | null;
    keyRef: string | null;
    fallbackUrl?: string | null;
    fallbackKeyRef?: string | null;
  }) {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      await api('/settings/geocode', { method: 'PATCH', body: next });
      setCfg(next);
      setSaved(true);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    /*
      ★ 给这一节起个名字。设置页上好几处都会出现「已启用」之类的字眼,
        用例满屏找文本会撞上别的区块 —— 而失败信息看起来像
        「这一节不见了」。
    */
    <section aria-label="地理编码">
      <h2 className="mb-1 text-xl font-semibold text-slate-900 dark:text-slate-100">地理编码</h2>
      <p className="mb-4 text-sm text-slate-500">
        {cfg.url
          ? '已启用 —— 建单时没给坐标的单会自动解析'
          : '没启用 —— 建单时没给坐标的单会进复核队列,要人在地图上一个个点'}
      </p>

      <div className="max-w-2xl space-y-3 rounded-xl border border-slate-200 p-4
                      dark:border-slate-700">
        <label className="block text-sm">
          <span className="text-slate-500">接口地址</span>
          <span className="block text-xs text-slate-400">
            <code>{'{address}'}</code> 会被替换成地址,<code>{'{key}'}</code> 替换成密钥。
            没有 <code>{'{address}'}</code> 的话每一单都会拿到同一个坐标
          </span>
          <input
            aria-label="接口地址"
            value={url}
            disabled={!canManage}
            onChange={(e) => {
              dirtyRef.current = true;
              setUrl(e.target.value);
            }}
            placeholder="https://…?address={address}&key={key}"
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5
                       font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
          />
        </label>

        <label className="block text-sm">
          <span className="text-slate-500">密钥引用</span>
          {/*
            ★ 这句话不能省。不写的话人一定会直接粘一个 key 进去,
              然后它永久躺在数据库里,连同每一次备份、每一次导出。
          */}
          <span className="block text-xs text-slate-400">
            填 <code>env:变量名</code>,不是密钥本身 —— 密钥放在服务器的环境变量里,
            不进数据库
          </span>
          <input
            aria-label="密钥引用"
            value={keyRef}
            disabled={!canManage}
            onChange={(e) => {
              dirtyRef.current = true;
              setKeyRef(e.target.value);
            }}
            placeholder="env:GEOCODE_API_KEY"
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5
                       font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
          />
        </label>

        {/*
          ★ 备用服务。主用那家挂掉的时候,建单不该跟着停 ——
            没有坐标的单上不了地图、司机导航不了,而这件事发生在
            客服正拿着电话的时候。
          ★ 但也不是一失败就切:备用通常不如主用准。连着失败几次才降级,
            次数在「设置 → 运营阈值」里改。
        */}
        <div className="border-t border-slate-100 pt-3 dark:border-slate-700/60">
          {/*
            ★ 这两栏叫「备选服务地址 / 备选服务密钥」,不叫「备用接口地址 / 备用密钥引用」:
              后者把上面那两栏的名字整个包在里面,`getByLabel('密钥引用')`
              会一下子匹配到两个元素 —— 读屏念出来也是两个几乎一样的名字。
              (同一类坑今天第二次了,第一次是建单页那个「找收件人」。)
          */}
          <div className="mb-2 text-sm text-slate-600 dark:text-slate-300">
            备用服务
            <span className="ml-2 text-xs text-slate-400">
              主用连着失败几次才走它(次数见「运营阈值」)。不填就是没有备用 ——
              主用不通时单子进复核队列
            </span>
          </div>
          <label className="block text-sm">
            <span className="text-slate-500">备选服务地址</span>
            <input
              aria-label="备选服务地址"
              value={fbUrl}
              disabled={!canManage}
              onChange={(e) => {
              dirtyRef.current = true;
              setFbUrl(e.target.value);
            }}
              placeholder="https://…?address={address}&key={key}"
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5
                         font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
            />
          </label>
          <label className="mt-2 block text-sm">
            <span className="text-slate-500">备选服务密钥</span>
            <input
              aria-label="备选服务密钥"
              value={fbKeyRef}
              disabled={!canManage}
              onChange={(e) => {
              dirtyRef.current = true;
              setFbKeyRef(e.target.value);
            }}
              placeholder="env:GEOCODE_FALLBACK_KEY"
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5
                         font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
            />
          </label>
        </div>

        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() =>
                void save({
                  url: url.trim() || null,
                  keyRef: keyRef.trim() || null,
                  fallbackUrl: fbUrl.trim() || null,
                  fallbackKeyRef: fbKeyRef.trim() || null,
                })
              }
              disabled={busy}
              className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white
                         disabled:opacity-40"
            >
              {busy ? '保存中…' : '保存'}
            </button>
            {cfg.url && (
              <button
                onClick={() => {
                  setUrl('');
                  setKeyRef('');
                  // 停用只停主用 —— 备用那一栏是另一个决定,别顺手清掉
                  void save({ url: null, keyRef: null });
                }}
                disabled={busy}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600
                           dark:border-slate-600 dark:text-slate-400"
              >
                停用
              </button>
            )}
            {saved && <span className="text-sm text-green-600 dark:text-green-400">已保存</span>}
          </div>
        )}

        {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

        {canManage && cfg.url && (
          <div className="border-t border-slate-100 pt-3 dark:border-slate-700/60">
            <div className="mb-1 text-xs text-slate-400">
              先试一个真实地址 —— 保存成功不等于通了,key 没开通 API 时服务返回的仍然是 200
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                aria-label="试算地址"
                value={probeAddr}
                onChange={(e) => setProbeAddr(e.target.value)}
                placeholder="Great North Road, Auckland"
                className="min-w-56 flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5
                           text-xs dark:border-slate-600 dark:bg-slate-800"
              />
              <button
                onClick={() => void test()}
                disabled={busy || !probeAddr.trim()}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600
                           disabled:opacity-40 dark:border-slate-600 dark:text-slate-400"
              >
                试一下
              </button>
            </div>
            {probe && (
              <div
                className={`mt-2 rounded-lg px-3 py-2 text-sm ${
                  probe.ok
                    ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400'
                    : 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400'
                }`}
              >
                {probe.text}
              </div>
            )}
          </div>
        )}

        {canManage && (
          <div className="border-t border-slate-100 pt-3 dark:border-slate-700/60">
            <div className="mb-1 text-xs text-slate-400">常见服务(点一下填进去,再按需要改)</div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.name}
                  onClick={() => setUrl(p.url)}
                  title={p.note}
                  className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs
                             text-slate-600 dark:border-slate-600 dark:text-slate-400"
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * 短信通知配置。
 *
 * ★ 和地理编码放在一起:两者是同一类东西(往外打的服务),
 *   配置形状也一样 —— URL + 密钥引用。分成两页只会让人多找一次。
 *
 * ★ 发不发由**功能开关**决定(设置 → 功能开关 → 短信通知收件人),
 *   这里只管「往哪发」。「没配网关」和「不想发」是两件事,
 *   混成一个的话,想暂时停发只能把网关地址删掉。
 */
export function SmsSettings({ canManage }: { canManage: boolean }) {
  const [url, setUrl] = useState('');
  const [keyRef, setKeyRef] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  /** 人动过没有 —— 回填请求回来晚了不能覆盖他填的(和上面那一节同一个坑) */
  const dirtyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void api<{
      tenant: { smsUrl?: string | null; smsKeyRef?: string | null; trackingUrl?: string | null };
    }>('/auth/me')
      .then((me) => {
        if (dirtyRef.current) return;
        setUrl(me.tenant.smsUrl ?? '');
        setKeyRef(me.tenant.smsKeyRef ?? '');
        setTrackingUrl(me.tenant.trackingUrl ?? '');
      })
      .catch(() => undefined);
  }, []);

  async function save() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      await api('/settings/sms', {
        method: 'PATCH',
        body: {
          url: url.trim() || null,
          keyRef: keyRef.trim() || null,
          trackingUrl: trackingUrl.trim() || null,
        },
      });
      setSaved(true);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label="短信通知">
      <h2 className="mb-1 text-xl font-semibold text-slate-900 dark:text-slate-100">短信通知</h2>
      <p className="mb-4 text-sm text-slate-500">
        {url
          ? '已配置 —— 取件和送达时给收件人发一条短信'
          : '没配网关 —— 收件人收不到通知,只能自己打电话问'}
        {'。发不发另由「功能开关 → 短信通知收件人」控制'}
      </p>

      <div className="max-w-2xl space-y-3 rounded-xl border border-slate-200 p-4
                      dark:border-slate-700">
        <label className="block text-sm">
          <span className="text-slate-500">网关地址</span>
          <span className="block text-xs text-slate-400">
            系统会 POST 一个 JSON:<code>{'{"to":"…","text":"…"}'}</code>
          </span>
          <input
            aria-label="网关地址"
            value={url}
            disabled={!canManage}
            onChange={(e) => {
              dirtyRef.current = true;
              setUrl(e.target.value);
            }}
            placeholder="https://sms.example.com/send"
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5
                       font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
          />
        </label>

        <label className="block text-sm">
          <span className="text-slate-500">密钥引用</span>
          {/* 短信网关的 token 泄了,别人可以用你的账号往外发短信 */}
          <span className="block text-xs text-slate-400">
            填 <code>env:变量名</code>,不是密钥本身
          </span>
          <input
            aria-label="短信密钥引用"
            value={keyRef}
            disabled={!canManage}
            onChange={(e) => {
              dirtyRef.current = true;
              setKeyRef(e.target.value);
            }}
            placeholder="env:SMS_TOKEN"
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5
                       font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
          />
        </label>

        <label className="block text-sm">
          <span className="text-slate-500">追踪页地址</span>
          {/*
            ★ 没有链接的话,客户唯一的下一步就是打电话。
              短信里只给一个单号,等于没给。
          */}
          <span className="block text-xs text-slate-400">
            <code>{'{code}'}</code> 会被替换成追踪码。留空的话短信里只有单号,
            客户只能打电话问
          </span>
          <input
            aria-label="追踪页地址"
            value={trackingUrl}
            disabled={!canManage}
            onChange={(e) => {
              dirtyRef.current = true;
              setTrackingUrl(e.target.value);
            }}
            placeholder="https://track.example.com/{code}"
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5
                       font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
          />
        </label>

        {canManage && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => void save()}
              disabled={busy}
              className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white
                         disabled:opacity-40"
            >
              {busy ? '保存中…' : '保存'}
            </button>
            {saved && <span className="text-sm text-green-600 dark:text-green-400">已保存</span>}
          </div>
        )}

        {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      </div>
    </section>
  );
}
